import { HANDOVER_LIMITS, type HandoverPhoto, handoverPhotoSchema } from '@sivan/shared';
import { Icon } from '../ui/Icon';
import { askConfirm } from '../ui/confirm';
import { useEffect, useEffectEvent, useId, useRef, useState } from 'react';
import { ApiError, NetworkError, apiUpload } from '../api/client';
import { PhotoPrepareError, preparePhoto } from './prepare-photo';

interface Upload {
  readonly key: string;
  readonly name: string;
  readonly progress: number;
  readonly error: string | null;
}

function uploadError(error: unknown): string {
  if (error instanceof PhotoPrepareError) return 'לא ניתן לקרוא את הקובץ כתמונה.';
  if (error instanceof NetworkError) return 'אין חיבור לשרת. התמונה לא הועלתה.';
  if (error instanceof ApiError) {
    if (error.status === 413) return 'התמונה גדולה מדי.';
    if (error.status === 415 || error.status === 422) return 'הקובץ אינו תמונה נתמכת.';
    if (error.status === 403) return 'אין לך הרשאה להעלות תמונות.';
    if (error.status === 503) return 'אחסון התמונות אינו זמין כרגע.';
  }
  return 'העלאת התמונה נכשלה.';
}

let counter = 0;

/**
 * Photos of one finding in the dialog: camera capture or photo library, upload progress, errors,
 * thumbnails and removal. Uploads go straight to private storage as unsaved uploads of the draft;
 * the draft save attaches them.
 */
export function PhotoField({
  photos,
  uploadPath,
  urlOf,
  onAdd,
  onRemove,
  onOpen,
  onBusy,
  error,
  initialFiles,
}: {
  readonly photos: readonly HandoverPhoto[];
  readonly uploadPath: string;
  readonly urlOf: (photo: HandoverPhoto) => string;
  readonly onAdd: (photo: HandoverPhoto) => void;
  readonly onRemove: (photo: HandoverPhoto) => void;
  readonly onOpen: (index: number) => void;
  readonly onBusy: (busy: boolean) => void;
  readonly error?: string | undefined;
  /** Files chosen before the field appeared: uploaded once, when it first shows. */
  readonly initialFiles?: readonly File[] | undefined;
}) {
  const id = useId();
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  /** The files of failed uploads, kept in memory only so that "ניסיון חוזר" sends them again. */
  const files = useRef(new Map<string, File>());
  const room =
    HANDOVER_LIMITS.photosPerFinding -
    photos.length -
    uploads.filter((u) => u.error === null).length;

  const update = (key: string, change: Partial<Upload>) => {
    setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...change } : u)));
  };
  const send = async (key: string, file: File) => {
    try {
      const prepared = await preparePhoto(file);
      const photo = await apiUpload(uploadPath, prepared, handoverPhotoSchema, (progress) => {
        update(key, { progress });
      });
      onAdd(photo);
      files.current.delete(key);
      setUploads((list) => list.filter((u) => u.key !== key));
    } catch (failure) {
      files.current.set(key, file);
      update(key, { error: uploadError(failure), progress: 0 });
    }
  };
  const start = async (picked: readonly File[]) => {
    const accepted = picked.slice(0, Math.max(0, room));
    if (accepted.length === 0) return;
    const items = accepted.map((file) => {
      counter += 1;
      return { key: `upload-${String(counter)}`, name: file.name, progress: 0, error: null };
    });
    setUploads((list) => [...list, ...items]);
    onBusy(true);
    for (const [index, file] of accepted.entries()) {
      const item = items[index];
      if (item !== undefined) await send(item.key, file);
    }
    onBusy(false);
  };
  const retry = async (key: string) => {
    const file = files.current.get(key);
    if (file === undefined) return;
    update(key, { error: null, progress: 0 });
    onBusy(true);
    await send(key, file);
    onBusy(false);
  };
  const startedWith = useRef(false);
  const startInitial = useEffectEvent(() => {
    if (initialFiles !== undefined && initialFiles.length > 0) void start(initialFiles);
  });
  useEffect(() => {
    if (startedWith.current) return;
    startedWith.current = true;
    startInitial();
  }, []);
  const dismiss = (key: string) => {
    files.current.delete(key);
    setUploads((list) => list.filter((x) => x.key !== key));
  };
  const pick = (input: HTMLInputElement | null) => {
    if (input === null) return;
    const files = [...(input.files ?? [])];
    input.value = '';
    void start(files);
  };

  return (
    <fieldset
      className="photo-field"
      aria-describedby={error === undefined ? undefined : `${id}-error`}
    >
      <legend>
        תמונות <span className="count">({photos.length})</span>
      </legend>
      {photos.length === 0 && uploads.length === 0 && (
        <p className="photo-field__required">
          <Icon name="camera" /> לכל ליקוי נדרשת לפחות תמונה אחת לפני סגירת המסירה.
        </p>
      )}
      {photos.length > 0 && (
        <ul className="photo-grid" aria-label="תמונות הליקוי">
          {photos.map((photo, index) => (
            <li key={photo.id} className="photo-grid__item">
              <button
                type="button"
                className="photo-grid__open"
                aria-label={`הצגת תמונה ${String(index + 1)}`}
                onClick={() => {
                  onOpen(index);
                }}
              >
                <img
                  src={urlOf(photo)}
                  alt=""
                  loading="lazy"
                  width={photo.width}
                  height={photo.height}
                />
              </button>
              <button
                type="button"
                className="photo-grid__remove"
                aria-label={`הסרת תמונה ${String(index + 1)}`}
                onClick={() => {
                  void askConfirm({
                    title: 'להסיר את התמונה?',
                    message: 'התמונה תוסר מהליקוי.',
                    confirmLabel: 'הסרת התמונה',
                    danger: true,
                  }).then((yes) => {
                    if (yes) onRemove(photo);
                  });
                }}
              >
                <Icon name="x" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {uploads.length > 0 && (
        <ul className="upload-list" aria-label="העלאות">
          {uploads.map((u) => (
            <li key={u.key} className={`upload${u.error === null ? '' : ' upload--error'}`}>
              {u.error === null ? (
                <>
                  <span className="upload__label">מעלה תמונה… {Math.round(u.progress * 100)}%</span>
                  <progress max={1} value={u.progress} aria-label={`התקדמות העלאה: ${u.name}`} />
                </>
              ) : (
                <>
                  <span className="upload__label" role="alert">
                    {u.error}
                  </span>
                  <button
                    type="button"
                    className="button button--small"
                    onClick={() => {
                      void retry(u.key);
                    }}
                  >
                    <Icon name="refresh" />
                    ניסיון חוזר
                  </button>
                  <button
                    type="button"
                    className="button button--small button--ghost"
                    onClick={() => {
                      dismiss(u.key);
                    }}
                  >
                    ויתור על התמונה
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="photo-field__actions">
        <button
          type="button"
          className="button"
          disabled={room <= 0}
          onClick={() => camera.current?.click()}
        >
          <Icon name="camera" />
          צילום
        </button>
        <button
          type="button"
          className="button"
          disabled={room <= 0}
          onClick={() => library.current?.click()}
        >
          <Icon name="images" />
          בחירה מהגלריה
        </button>
        {/* Hidden native pickers: the camera on supported phones, the photo library otherwise. */}
        <input
          ref={camera}
          type="file"
          accept="image/*"
          capture="environment"
          className="visually-hidden"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            pick(e.currentTarget);
          }}
        />
        <input
          ref={library}
          type="file"
          accept="image/*"
          multiple
          className="visually-hidden"
          tabIndex={-1}
          aria-label="בחירת תמונות"
          data-testid="photo-library-input"
          onChange={(e) => {
            pick(e.currentTarget);
          }}
        />
      </div>
      <p className="hint">
        עד {HANDOVER_LIMITS.photosPerFinding} תמונות לליקוי. התמונות מוקטנות לפני ההעלאה, ונתוני
        מיקום מוסרים מהן.
      </p>
      {error !== undefined && (
        <p className="field__error" id={`${id}-error`}>
          {error}
        </p>
      )}
    </fieldset>
  );
}
